import React, { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { useCamara } from "@/components/Camara";

const PANTALLAS = [
  { icon: Icons.FolderHeart, titulo: "Todo lo importante, en un solo sitio", texto: "Documentos, citas, planes, recordatorios y gestiones personales." },
  { icon: Icons.LayoutGrid, titulo: "AL DÍA lo organiza por ti", texto: "Viviendas, vehículos, ocio y reservas, familia, mascotas y servicios personales." },
  { icon: Icons.BellRing, titulo: "Te avisa antes de que se te pase algo", texto: "Citas, planes, vencimientos y gestiones recurrentes." },
  { foto: true, titulo: "Haz una foto. AL DÍA hace el resto.", texto: "Fotografía una carta, recibo, entrada, reserva o documento. AL DÍA lo lee, detecta la información importante y te ayuda a organizarla." },
  { demo: true, titulo: "Así lo organiza AL DÍA" },
  { icon: Icons.ShieldCheck, titulo: "Tú tienes siempre el control", texto: "AL DÍA propone, organiza y recuerda. Tú confirmas las acciones importantes." },
];

const Foto = ({ onFoto }) => {
  const elegir = (e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) onFoto(f); };
  const camara = useCamara(elegir);
  return (
    <div className="flex flex-col items-center gap-4">
      {camara.inputs}
      <button type="button" onClick={camara.abrir} data-testid="intro-hacer-foto" className="w-44 h-44 rounded-full bg-[#3A6B56] shadow-xl shadow-[#3A6B56]/30 ring-8 ring-[#E8F0EC] flex flex-col items-center justify-center gap-2 text-white active:scale-95 transition-transform">
        <Icons.Camera size={64} strokeWidth={1.8} />
        <span className="font-heading text-xl font-bold">Hacer una foto</span>
      </button>
      <label data-testid="intro-elegir-imagen" className="cursor-pointer min-h-[44px] px-4 inline-flex items-center gap-2 text-base font-semibold text-[#3A6B56] underline underline-offset-4">
        <Icons.Image size={18} /> o elegir una imagen
        <input type="file" accept="image/*,.pdf,application/pdf" className="sr-only" onChange={elegir} data-testid="intro-imagen-input" />
      </label>
      {camara.aviso}
    </div>
  );
};

const Ticket = () => (
  <div className="w-24 shrink-0 bg-white rounded-md shadow-md border border-[#E5DFD5] px-2 py-2 rotate-[-4deg] font-mono text-[8px] leading-tight text-[#4A5550]" aria-hidden="true">
    <p className="font-bold text-[10px] text-center text-[#1C2421]">REPSOL</p>
    <p className="text-center">E.S. Ejemplo</p>
    <p className="mt-1">08/10/2026</p>
    <p>Gasolina 95</p>
    <p>42,30 L</p>
    <div className="border-t border-dashed border-[#C4BDB0] my-1" />
    <p className="font-bold text-[#1C2421] flex justify-between"><span>TOTAL</span><span>68,97€</span></p>
  </div>
);

const DATOS_DEMO = [["Proveedor", "Repsol"], ["Fecha", "8 de octubre de 2026"], ["Importe", "68,97 €"], ["Categoría sugerida", "Gasolina"], ["Vehículo relacionado", "Kia Sportage"]];
const PROPUESTAS_DEMO = ["Guardarlo en Gastos", "Asociarlo al vehículo", "Conservar el justificante", "Incluirlo en el resumen mensual"];

const Demo = () => (
  <div className="w-full text-left" data-testid="intro-demo">
    <div className="flex items-center gap-3 justify-center [@media(max-height:700px)]:-my-2 [@media(max-height:700px)]:scale-[0.85]">
      <Ticket />
      <Icons.ArrowRight size={22} className="text-[#3A6B56] shrink-0" />
      <span className="text-sm font-semibold text-[#3A6B56] leading-tight">AL DÍA lee el ticket</span>
    </div>
    <div className="mt-3 bg-white border border-[#E5DFD5] rounded-2xl px-3 py-2.5 shadow-sm" data-testid="intro-demo-entendido">
      <p className="font-heading font-bold text-base text-[#1C2421] flex items-center gap-2"><Icons.Sparkles size={18} className="text-[#3A6B56]" /> He entendido este documento</p>
      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-sm">
        {DATOS_DEMO.map(([k, v]) => <React.Fragment key={k}><dt className="text-[#6E7A74]">{k}</dt><dd className="font-semibold text-[#1C2421]">{v}</dd></React.Fragment>)}
      </dl>
    </div>
    <div className="mt-2 bg-[#E8F0EC] rounded-2xl px-3 py-2.5" data-testid="intro-demo-propone">
      <p className="font-heading font-bold text-base text-[#1C2421]">AL DÍA te propone</p>
      <ul className="mt-1 space-y-0.5 text-sm text-[#1C2421]">
        {PROPUESTAS_DEMO.map((t) => <li key={t} className="flex items-center gap-2"><Icons.CheckCircle2 size={16} className="text-[#3A6B56] shrink-0" />{t}</li>)}
      </ul>
    </div>
  </div>
);

// Introducción breve (6 pantallas). onFin(resultado, archivo?) cierra y la marca como vista.
export const Introduccion = ({ onFin, publica = false }) => {
  const [i, setI] = useState(0);
  const toque = useRef(null);
  const p = PANTALLAS[i];
  const ultima = i === PANTALLAS.length - 1;
  const ir = (n) => setI(Math.max(0, Math.min(PANTALLAS.length - 1, n)));
  const fin = (x) => { const dx = x - (toque.current ?? x); toque.current = null; if (Math.abs(dx) > 60) ir(i + (dx < 0 ? 1 : -1)); };
  return (
    <div className="fixed inset-0 z-[90] bg-[#FAF8F5] flex flex-col" data-testid="intro" role="dialog" aria-modal="true" aria-label="Introducción a AL DÍA"
      onTouchStart={(e) => { toque.current = e.touches[0].clientX; }} onTouchEnd={(e) => fin(e.changedTouches[0].clientX)}
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="flex justify-between items-center px-5 pt-4">
        <span className="font-heading font-extrabold text-lg tracking-tight text-[#3A6B56]">AL DÍA</span>
        <button data-testid="intro-saltar" onClick={() => onFin("saltada")} className="min-h-[48px] px-4 rounded-full text-base font-semibold text-[#4A5550] hover:bg-[#F2EDE4]">Saltar</button>
      </div>
      {p.demo ? (
        <div key={i} className="flex-1 min-h-0 overflow-y-auto flex flex-col px-6 py-2 max-w-md mx-auto w-full aldia-fade-up" data-testid={`intro-pantalla-${i + 1}`}>
          <div className="my-auto flex flex-col items-center text-center">
          <span className="px-3 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-bold uppercase tracking-wide" data-testid="intro-demo-etiqueta">Ejemplo de demostración</span>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421] tracking-tight mt-2 mb-3 leading-tight" data-testid="intro-titulo">{p.titulo}</h1>
          <Demo />
          </div>
        </div>
      ) : (
      <div key={i} className="flex-1 flex flex-col items-center justify-center px-7 text-center max-w-md mx-auto w-full aldia-fade-up" data-testid={`intro-pantalla-${i + 1}`}>
        {p.foto && publica ? (
          <button data-testid="intro-crear-cuenta" onClick={() => onFin("registro")} className="min-h-[60px] px-8 rounded-2xl bg-[#3A6B56] text-white font-bold text-xl hover:bg-[#2E5645] shadow-lg shadow-[#3A6B56]/25">Crear mi cuenta</button>
        ) : p.foto ? <Foto onFoto={(f) => onFin("foto", f)} /> : (
          <span className="w-32 h-32 rounded-full bg-[#E8F0EC] flex items-center justify-center"><p.icon size={60} className="text-[#3A6B56]" strokeWidth={1.7} /></span>
        )}
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C2421] tracking-tight mt-8 leading-tight" data-testid="intro-titulo">{p.titulo}</h1>
        <p className="text-xl text-[#4A5550] mt-4 leading-relaxed">{p.texto}</p>
        {p.foto && !publica && <p className="text-base text-[#6E7A74] mt-4">También puedes subir un archivo o introducirlo manualmente.</p>}
      </div>
      )}
      <div className="px-5 pb-6 pt-2 max-w-md mx-auto w-full">
        <div className="flex justify-center gap-2 mb-5" data-testid="intro-puntos">
          {PANTALLAS.map((_, n) => <span key={n} className={`h-2.5 rounded-full transition-all duration-300 ${n === i ? "w-8 bg-[#3A6B56]" : "w-2.5 bg-[#D6CFC2]"}`} />)}
        </div>
        <div className="flex gap-3">
          {i > 0 && <button data-testid="intro-anterior" onClick={() => ir(i - 1)} className="flex-1 min-h-[56px] rounded-2xl bg-[#F2EDE4] border border-[#E5DFD5] text-[#1C2421] font-semibold text-lg">Anterior</button>}
          {ultima
            ? <button data-testid="intro-empezar" onClick={() => onFin("completada")} className="flex-[2] min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-bold text-lg hover:bg-[#2E5645]">{publica ? "Terminar" : "Empezar"}</button>
            : <button data-testid="intro-siguiente" onClick={() => ir(i + 1)} className="flex-[2] min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-bold text-lg hover:bg-[#2E5645]">Siguiente</button>}
        </div>
      </div>
    </div>
  );
};
